import {
  Button,
  Container,
  Dialog,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Slide,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { branchMenuFlag } from "common/constants/feature-toggles";
import { useEffect, useState } from "react";
import { IconStore } from "components/icons/IconStore";
import { iconStyle } from "components/mobile-navigation/styles";
import { makeStyles } from "@mui/styles";
import { colors } from "common";
import { BranchListHeader } from "./BranchListHeader";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import { isMobile } from "react-device-detect";
import { useBranchSelection } from "hooks/use-branch-selection";
import { useLoginStatus } from "hooks/useLoginStatus";
import { useRouter } from "next/router";
import { navigationRoutes } from "common/constants/navigation-routes";
import { TTL } from "utils/constants";
import { useRefetchWithTTL } from "hooks/use-refetch-with-ttl";

const useStyles = makeStyles({
  branchButton: {
    position: "fixed",
    padding: 15,
    bottom: 20,
    left: 20,
    zIndex: 1,
    background: "white",
    transition: "opacity 0.4s ease",
    boxShadow: "1px 1px 5px grey",
    "&:hover": {
      background: "white",
    },
  },
  branchList: {
    color: colors.primary,
    padding: "20",
  },
  branchName: {
    color: colors.skyBlue,
  },
  branchListItem: {
    // border: `1px solid #D8E0E9`,
    borderRadius: "5px",
    marginTop: 10,
  },
  itemHoverCursor: {
    cursor: "pointer",
  },
});

// Fancy Dialog Transistion
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const BranchMenu = () => {
  const classes = useStyles();
  const { pathname } = useRouter();
  const {
    branchOptions,
    selectedBranch,
    branchOptionsLoading,
    selectedBranchId,
    lastFetchedBranchesAt,

    getActiveBranches,
    setAsDefaultBranch,
    setAsCurrentBranch,
  } = useBranchSelection();

  const { isUserLogin } = useLoginStatus();

  const [branchOpen, toggleBranchOpen] = useState(false);

  useRefetchWithTTL({
    lastFetched: lastFetchedBranchesAt,
    ttl: TTL.semiStatic,
    fetchAction: () => getActiveBranches(),
  });

  // Set a default branch if the user is not logged in or hasn't selected one.
  useEffect(() => {
    if (branchOptions[0] && !selectedBranchId) {
      const defaultBranch = branchOptions.find((branch) => branch.is_default);
      setAsCurrentBranch(defaultBranch || null);
      setAsDefaultBranch(defaultBranch || null);
    }
  }, [
    branchOptions,
    isUserLogin,
    selectedBranchId,
    setAsCurrentBranch,
    setAsDefaultBranch,
  ]);

  if (!branchMenuFlag || pathname === navigationRoutes.checkout) {
    return null;
  }

  return !isMobile ? (
    <Container>
      {branchOptionsLoading ? (
        <Button
          disabled
          className={classes.branchButton}
          sx={{ width: "20em" }}
        >
          <CircularProgress color="secondary" />
        </Button>
      ) : (
        <Button
          // cannot be clicked if options are unavailable
          disabled={!branchOptions[0]}
          onClick={() => toggleBranchOpen(!branchOpen)}
          className={classes.branchButton}
        >
          <IconStore sx={iconStyle} />
          {!selectedBranch ? (
            <Typography variant={"h6"} marginLeft={2} fontWeight={"bold"}>
              Select a Branch
            </Typography>
          ) : (
            <Typography variant={"h6"} marginLeft={2} fontWeight={"bold"}>
              {`You're in `}
              <span className={classes.branchName}>
                {selectedBranch?.branch_name}
              </span>
              {" Branch."}
            </Typography>
          )}
        </Button>
      )}
      <Dialog open={branchOpen} TransitionComponent={Transition}>
        <List
          className={classes.branchList}
          sx={{ padding: branchOpen ? "10px" : "15px" }}
        >
          <ListItem disablePadding>
            <BranchListHeader
              toggleBranchOpen={toggleBranchOpen}
              branchName={selectedBranch?.branch_name || ""}
              branchOpen={branchOpen}
            />
          </ListItem>
          {branchOptions.map((b) => (
            <ListItemButton
              className={classes.branchListItem}
              key={b.branch_name}
              onClick={() => {
                toggleBranchOpen(!branchOpen);
                setAsCurrentBranch(b);
              }}
              sx={{
                border: `${b.id === selectedBranch?.id ? "3px" : "1px"} solid ${
                  b.id === selectedBranch?.id ? colors.lightBlue[500] : "black"
                }`,
                opacity: !branchOpen ? 0 : 1,
                height: !branchOpen ? "0px" : "initial",
                transition: "all 0.4s",
              }}
            >
              <ListItemIcon>
                <IconStore fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {b.branch_name == selectedBranch.branch_name ? (
                  <Typography fontWeight={600}>
                    {`You're in `}
                    <span style={{ color: colors.skyBlue }}>
                      {b.branch_name}
                    </span>
                    {" Branch."}
                  </Typography>
                ) : (
                  <Typography fontWeight={600}>
                    {`Switch to `}
                    <span style={{ color: colors.skyBlue }}>
                      {b.branch_name}
                    </span>
                    {" Branch."}
                  </Typography>
                )}
              </ListItemText>
            </ListItemButton>
          ))}
        </List>
      </Dialog>
    </Container>
  ) : null;
};
